import React, { useState, useEffect, useContext, useRef, useMemo, useCallback } from 'react';
import { Grid, Typography, Box, IconButton, MenuItem, Select, FormControl, TextField, Checkbox, Table, TableBody, TableCell, TableHead, TableRow, Tooltip, Accordion, AccordionSummary, AccordionDetails, Divider, FormControlLabel } from '@mui/material';
import { GridExpandMoreIcon } from '@mui/x-data-grid';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import CloseIcon from '@mui/icons-material/Close';
import { ProfileContext, ProfileContextPayload } from '../../../Context/ProfileDetailsContext';
import forwardArrow from '../../../assets/Images/forwardArrow.svg';
import styles from '../../../Styles/ComponentStyles/AIPdfExtractor/AIPdfExtractor.module.scss';

// Define interfaces
interface Page {
  pageId: string;
  pageNumber: number;
  imageUrl: string;
  dataIndex: number;
  pageIndex: number;
}

interface PageComponentProps {
  page: Page;
  isActive: boolean;
  panPosition: { x: number; y: number };
  zoomLevel: number;
  setPageRef: (el: HTMLDivElement | null, pageId: string) => void;
  handlePageClick: (pageId: string) => void;
  imageContainerRef: React.RefObject<HTMLDivElement>;
  isPanning: boolean;
  handleMouseDown: (e: React.MouseEvent) => void;
  handleMouseMove: (e: React.MouseEvent) => void;
  handleMouseUp: () => void;
}

// Memoized Page Component with custom equality check
const PageComponent = React.memo(
  ({
    page,
    isActive,
    panPosition,
    zoomLevel,
    setPageRef,
    handlePageClick,
    imageContainerRef,
    isPanning,
    handleMouseDown,
    handleMouseMove,
    handleMouseUp,
  }: PageComponentProps) => {
    return (
      <Grid
        item
        xs={12}
        className={`${styles.pageContainer} ${isActive ? styles.activePageContainer : styles.inactivePageContainer}`}
        ref={(el) => setPageRef(el, page.pageId)}
        onClick={(e) => {
          e?.stopPropagation();
          handlePageClick(page.pageId);
        }}
      >
        <Grid container gap={1} padding={'10px'}>
          <Grid item xs={12} className={styles.pageNumberTxt}>
            Page {page.pageNumber}
          </Grid>
          <Grid
            item
            xs={12}
            className={styles.imageZoomContainer}
            style={{
              overflow: 'hidden',
              position: 'relative',
              width: '100%',
              paddingBottom: '112.5%',
            }}
            ref={isActive ? imageContainerRef : null}
          >
            {isActive && zoomLevel > 100 && (
              <div
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseUp}
                style={{
                  cursor: isPanning ? "grabbing" : "grab",
                  zIndex: 10,
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  touchAction: "none"
                }}
              />
            )}
            <div
              style={{
                position: 'absolute',
                top: panPosition.y,
                left: panPosition.x,
                transform: `scale(${zoomLevel / 100})`,
                transformOrigin: 'top left',
                width: '100%',
                overflow: 'hidden',
              }}
            >
              <img
                src={page.imageUrl}
                alt={`PDF page ${page.pageNumber}`}
                width="100%"
                height="100%"
                style={{
                  pointerEvents: 'none',
                  objectFit: 'contain',
                }}
              />
            </div>
          </Grid>
        </Grid>
      </Grid>
    );
  },
  (prevProps, nextProps) =>
    prevProps.page.pageId === nextProps.page.pageId &&
    prevProps.isActive === nextProps.isActive &&
    prevProps.zoomLevel === nextProps.zoomLevel &&
    prevProps.panPosition.x === nextProps.panPosition.x &&
    prevProps.panPosition.y === nextProps.panPosition.y
);

const PreviewAndExtractComponent: React.FC = () => {
  const context = useContext(ProfileContext) as ProfileContextPayload;
  const {
    extractedTablesData,
    setExtractedTablesData,
    selectedFields,
    setSelectedFields,
    selectedColumns,
    setSelectedColumns,
  } = context;

  const panPositionRef = useRef({ x: 0, y: 0 });
  const startPanPositionRef = useRef({ x: 0, y: 0 });
  const isPanningRef = useRef(false);

  const [zoomLevel, setZoomLevel] = useState<number>(100);
  const [activePage, setActivePage] = useState<string | null>(null);
  const [isPanning, setIsPanning] = useState<boolean>(false);
  const [panPosition, setPanPosition] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
  const imageContainerRef = useRef<HTMLDivElement>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);

  const fieldOptions = useMemo(() => [
    { value: 'Field1', label: 'IUL Fee Detail' },
    { value: 'Field2', label: 'IUL Account Value Detail' },
    { value: 'Field3', label: 'IUL Death Benefits' },
  ], []);

  const pageData = useMemo<Page[]>(() => {
    return extractedTablesData?.flatMap((dataItem: any, dataIndex: number) =>
      dataItem?.pdf_extraction_page_details?.map((page: any, pageIndex: number) => ({
        pageId: `${dataIndex}-${pageIndex}`,
        pageNumber: page.page_number,
        imageUrl: page.google_storage_file_self_link,
        dataIndex,
        pageIndex,
      })) || []
    ) || [];
  }, [extractedTablesData]);

  const setPageRef = useCallback((element: HTMLDivElement | null, pageId: string) => {
    if (element && observerRef.current) {
      element.dataset.pageId = pageId;
      observerRef.current.observe(element);
    }
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const visiblePageId = (entry.target as HTMLElement).dataset.pageId;
          if (visiblePageId) setActivePage(visiblePageId);
        }
      });
    }, { threshold: 0.5 });

    observerRef.current = observer;
    return () => observerRef.current?.disconnect();
  }, []);

  useEffect(() => {
    if (!activePage && pageData.length > 0) {
      setActivePage(pageData[0].pageId);
    }
  }, [pageData, activePage]);

  useEffect(() => {
    if (zoomLevel === 100) {
      // Reset both state and refs
      setPanPosition({ x: 0, y: 0 });
      panPositionRef.current = { x: 0, y: 0 };
      startPanPositionRef.current = { x: 0, y: 0 };
    }
  }, [zoomLevel]);

  // New panning logic
  const handleMouseDown = (e: React.MouseEvent) => {
    if (zoomLevel <= 100 || !imageContainerRef.current) return;

    e.preventDefault();
    e.stopPropagation();

    // Update both state and ref for isPanning
    setIsPanning(true);
    isPanningRef.current = true;

    // Store starting positions in refs
    startPanPositionRef.current = {
      x: e.clientX - panPositionRef.current.x,
      y: e.clientY - panPositionRef.current.y
    };
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isPanningRef.current || !imageContainerRef.current) return;

    e.preventDefault();
    e.stopPropagation();

    // Get container dimensions
    const containerRect = imageContainerRef.current.getBoundingClientRect();

    // Calculate max boundaries
    const maxX = ((zoomLevel - 100) / 100) * containerRect.width;
    const maxY = ((zoomLevel - 100) / 100) * containerRect.height;

    // Calculate new position
    const newX = e.clientX - startPanPositionRef.current.x;
    const newY = e.clientY - startPanPositionRef.current.y;

    // Constrain the position
    const constrainedX = Math.max(Math.min(newX, 0), -maxX);
    const constrainedY = Math.max(Math.min(newY, 0), -maxY);

    // Update both state and ref
    panPositionRef.current = { x: constrainedX, y: constrainedY };
    setPanPosition(panPositionRef.current);
  };

  const handleMouseUp = () => {
    if (!isPanningRef.current) return;

    isPanningRef.current = false;
    setIsPanning(false);
  };

  const handleZoomIn = useCallback(() => {
    setZoomLevel((prev) => Math.min(prev + 25, 300));
  }, []);

  const handleZoomOut = useCallback(() => {
    setZoomLevel((prev) => Math.max(prev - 25, 100));
  }, []);

  const handlePageClick = useCallback((pageId: string) => {
    setActivePage(pageId);
  }, []);

  const transformedData = useMemo(() => {
    return extractedTablesData?.flatMap((pageData: any, pageIndex: number) => {
      // const pageNumber = pageData.pdf_extraction_page_details?.[0]?.page_number || null;
      const imageUrl = pageData.pdf_extraction_page_details?.[0]?.google_storage_file_self_link || null;

      return (pageData.extractedData.tables || []).map((table: any, tableIndex: number) => ({
        pageId: `${pageIndex}-${pageData.extractedData?.tables?.[tableIndex]?.page_number - 1 || 0}`,
        tableId: `${pageIndex}-${tableIndex}`,
        table_name: table.table_name,
        page_number: table?.extracted_from_pages?.length > 1
          ? table.extracted_from_pages.join(',')
          : table?.extracted_from_pages?.[0],
        imageUrl,
        columns: Object.keys(table.table_data?.[0] || {}),
        rows: table.table_data || [],
      }));
    }) || [];
  }, [extractedTablesData]);

  const getAvailableFieldOptions = useCallback(
    (currentTableId: string) => {
      const selectedValues = Object.entries(selectedFields)
        .filter(([tableId]) => tableId !== currentTableId)
        .map(([_, value]) => value);

      return fieldOptions.filter(
        (option) =>
          !selectedValues.includes(option.value) ||
          option.value === selectedFields[currentTableId]
      );
    },
    [selectedFields, fieldOptions]
  );

  const handleFieldSelection = useCallback(
    (tableId: string, value: string) => {
      setSelectedFields((prev) => ({ ...prev, [tableId]: value }));
      setSelectedColumns((prev) => ({ ...prev, [tableId]: {} }));
    },
    [setSelectedFields, setSelectedColumns]
  );

  const handleColumnSelection = useCallback(
    (tableId: string, header: string, isMultiple: boolean) => {
      setSelectedColumns((prev) => {
        const tableCols = new Set(Object.keys(prev[tableId] || {}));
        if (tableCols.has(header)) tableCols.delete(header);
        else {
          if (!isMultiple) tableCols.clear();
          tableCols.add(header);
        }
        const updatedCols = Array.from(tableCols).reduce((obj, col) => {
          obj[col] = true;
          return obj;
        }, {} as Record<string, boolean>);
        return { ...prev, [tableId]: updatedCols };
      });
    },
    [setSelectedColumns]
  );

  const handleCellChange = useCallback(
    (pageIndex: number, tableIndex: number, rowIndex: number, header: string, value: string) => {
      setExtractedTablesData((prev: any) => {
        const newData = [...prev];
        const page = { ...newData[pageIndex] };
        const tables = [...page.extractedData.tables];
        const table = { ...tables[tableIndex] };
        const tableData = [...table.table_data];
        const row = { ...tableData[rowIndex] };

        row[header] = value;
        tableData[rowIndex] = row;
        table.table_data = tableData;
        tables[tableIndex] = table;
        page.extractedData.tables = tables;
        newData[pageIndex] = page;

        return newData;
      });
    },
    [setExtractedTablesData]
  );

  return (
    <Grid container>
      <Grid
        item
        xs={3.5}
        className={styles.selectPageSection}
        sx={{
          height: "75vh",
          overflowY: "auto",
        }}
      >
        <Grid container gap={2}>
          <Grid
            item
            padding="10px 20px 10px 20px"
            xs={12}
            sx={{
              position: "sticky",
              top: 0,
              overflowY: "auto",
              zIndex: 10,
              backgroundColor: "rgb(88, 84, 84)",
            }}
          >
            <Grid container alignItems={"center"} justifyContent={"space-between"}>
              <Grid item className={styles.selectedPagesTxt}>
                Selected Pages
              </Grid>
              <Grid
                item
                className={styles.zoomControls}
                style={{ display: "flex", alignItems: "center", gap: "8px" }}
              >
                <IconButton
                  size="small"
                  onClick={handleZoomOut}
                  style={{ border: "1px solid #fff", borderRadius: "4px", color: "#fff" }}
                >
                  <ZoomOutIcon fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={handleZoomIn}
                  style={{ border: "1px solid #fff", borderRadius: "4px", color: "#fff" }}
                >
                  <ZoomInIcon fontSize="small" />
                </IconButton>
                <FormControl size="small" sx={{ minWidth: 90 }}>
                  <Select
                    value={zoomLevel}
                    onChange={(e) => {
                      const newZoom = Number(e.target.value);
                      setZoomLevel(newZoom);
                      if (newZoom === 100) {
                        setPanPosition({ x: 0, y: 0 });
                      }
                    }}
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          maxHeight: 175,
                        },
                      },
                    }}
                    sx={{
                      height: "32px",
                      textAlign: "center",
                      fontSize: "14px",
                      color: "#fff",
                      ".MuiOutlinedInput-notchedOutline": {
                        borderColor: "#fff",
                      },
                      ".MuiSvgIcon-root": {
                        color: "#fff",
                      },
                    }}
                  >
                    {[100, 125, 150, 175, 200, 225, 250, 275, 300].map((percentage) => (
                      <MenuItem key={percentage} value={percentage}>
                        {percentage}%
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Grid>
          <Grid item padding="0px 20px 20px 20px" xs={12}>
            {pageData && pageData.length > 0 ? (
              <Grid item xs={12}>
                <Grid container gap={3}>
                  {pageData.map((page: Page) => (
                    <PageComponent
                      key={page.pageId}
                      page={page}
                      isActive={activePage === page.pageId}
                      panPosition={panPosition}
                      zoomLevel={zoomLevel}
                      setPageRef={setPageRef}
                      handlePageClick={handlePageClick}
                      imageContainerRef={imageContainerRef}
                      isPanning={isPanning}
                      handleMouseDown={handleMouseDown}
                      handleMouseMove={handleMouseMove}
                      handleMouseUp={handleMouseUp}
                    />
                  ))}
                </Grid>
              </Grid>
            ) : (
              <Typography className={styles.noDataFoundTxt}>No Data Found</Typography>
            )}
          </Grid>
        </Grid>
      </Grid>

      <Grid item xs={8.5} height={"75vh"} className={styles.previewSection}>
        <Grid container padding="20px" gap={3}>
          <Grid item xs={12} className={styles.extractedTablesTxt}>
            Extracted tables
          </Grid>
          <Grid item xs={12}>
            {transformedData && transformedData.length > 0 ? (
              <>
                {transformedData.map((table: any) => (
                  <Accordion key={table.tableId} className={styles.accordionStyle}>
                    <AccordionSummary expandIcon={<GridExpandMoreIcon />}>
                      <Grid container alignItems="center" gap={"12px"}>
                        <Grid item className={styles.accordianSummaryTxt}>
                          {table?.table_name}
                        </Grid>
                        <Divider sx={{ borderColor: "rgba(181, 181, 181, 1)" }} orientation="vertical" flexItem />
                        <Grid
                          item
                          className={styles.accordianSummaryTxt}
                          sx={{ color: "rgba(130, 130, 130, 1)" }}
                        >
                          Page {table?.page_number}
                        </Grid>
                        <Divider sx={{ borderColor: "rgba(181, 181, 181, 1)" }} orientation="vertical" flexItem />
                        <Grid item className={styles.accordianSummaryTxt}>
                          <Grid container gap={"10px"} alignItems={"center"}>
                            <Grid item>Extract to</Grid>
                            <Grid item>
                              <img height={"10px"} src={forwardArrow} alt="" />
                            </Grid>
                            <Grid item>
                              <Box display="flex" alignItems="center" gap={1}>
                                <FormControl
                                  sx={{ minWidth: 200 }}
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <Select
                                    displayEmpty
                                    value={selectedFields[table.tableId] || ""}
                                    onChange={(e) => handleFieldSelection(table.tableId, e.target.value as string)}
                                    renderValue={(selected) => {
                                      if (!selected) return "Select Field";
                                      const selectedOption = fieldOptions.find((opt) => opt.value === selected);
                                      return selectedOption ? selectedOption.label : "Select Field";
                                    }}
                                    className={styles.extractFieldSelectDropdown}
                                    onClick={(e) => e.stopPropagation()}
                                    endAdornment={
                                      selectedFields[table.tableId] && (
                                        <IconButton
                                          size="small"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleFieldSelection(table.tableId, "");
                                          }}
                                          sx={{ marginRight: "15px" }}
                                        >
                                          <CloseIcon fontSize="small" />
                                        </IconButton>
                                      )
                                    }
                                  >
                                    {getAvailableFieldOptions(table.tableId).map((option) => (
                                      <MenuItem
                                        key={option.value}
                                        value={option.value}
                                        disabled={
                                          option.value !== selectedFields[table.tableId] &&
                                          Object.values(selectedFields).includes(option.value)
                                        }
                                      >
                                        {option.label}
                                        {option.value !== selectedFields[table.tableId] &&
                                          Object.values(selectedFields).includes(option.value) &&
                                          " (Already selected)"}
                                      </MenuItem>
                                    ))}
                                  </Select>
                                </FormControl>
                              </Box>
                            </Grid>
                          </Grid>
                        </Grid>
                      </Grid>
                    </AccordionSummary>

                    <AccordionDetails sx={{ padding: "0px 16px 8px 16px" }}>
                      {table.rows?.length > 0 ? (
                        <Box sx={{ width: "100%", overflowX: "auto" }} className={styles.tableContainer}>
                          <Table sx={{ minWidth: "100%" }} className={styles.tableWithGaps}>
                            <TableHead>
                              <TableRow>
                                {table.columns.map((header: string, headerIndex: number) => (
                                  <TableCell
                                    key={headerIndex}
                                    className={styles.previewTableHeaderCell}
                                    sx={{ position: "sticky", top: 0, zIndex: 2 }}
                                  >
                                    <Box className={styles.headerFlexContainer}>
                                      <FormControlLabel
                                        control={
                                          <Checkbox
                                            size="small"
                                            checked={selectedColumns[table.tableId]?.[header] || false}
                                            onChange={() =>
                                              handleColumnSelection(
                                                table.tableId,
                                                header,
                                                selectedFields[table.tableId] === "Field1"
                                              )
                                            }
                                            disabled={!selectedFields[table.tableId]}
                                            onClick={(e) => e.stopPropagation()}
                                          />
                                        }
                                        label=""
                                        className={styles.headerCheckbox}
                                      />
                                      <Tooltip title={header} arrow>
                                        <Typography variant="body2" className={styles.headerText}>
                                          {header}
                                        </Typography>
                                      </Tooltip>
                                    </Box>
                                  </TableCell>
                                ))}
                              </TableRow>
                            </TableHead>

                            <TableBody>
                              {table.rows.map((row: any, rowIndex: number) => (
                                <TableRow key={rowIndex}>
                                  {table.columns.map((header: string, cellIndex: number) => (
                                    <TableCell key={`${rowIndex}-${cellIndex}`} className={styles.previewTableBodyCell}>
                                      <TextField
                                        variant="standard"
                                        value={row[header] || ""}
                                        onChange={(e) => {
                                          const [pageIndex, tableIndex] = table.tableId.split("-").map(Number);
                                          handleCellChange(pageIndex, tableIndex, rowIndex, header, e.target.value);
                                        }}
                                        className={styles.editableCell}
                                        InputProps={{
                                          disableUnderline: true,
                                          className: styles.noBorder,
                                        }}
                                      />
                                    </TableCell>
                                  ))}
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </Box>
                      ) : (
                        <Typography>No Data Available</Typography>
                      )}
                    </AccordionDetails>
                  </Accordion>
                ))}
              </>
            ) : (
              <Typography className={styles.noDataFoundTxt}>No Data Found</Typography>
            )}
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};

export default PreviewAndExtractComponent;